import {
  BarChart3,
  Brain,
  Building2,
  FileText,
  CalendarClock,
  FolderTree,
  GraduationCap,
  Layers,
  LayoutDashboard,
  ListChecks,
  Server,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Timer,
  UserRound,
  Users,
} from 'lucide-react';
import type { AccessCapability, PortalKey } from '@services/access';
import { ADMIN_VISIBILITY_FILTERS, ORG_VISIBILITY_FILTERS } from '@shared/ui/layout';
import type { UserRole } from '@shared/types';

export interface NavItem {
  label: string;
  path: string;
  icon: React.ElementType;
  badge?: string;
  i18nKey?: string;
  /** Overrides the test id derived from the key. */
  testId?: string;
  children?: NavItem[];
}

export type MenuKey =
  | 'dashboard'
  | 'users'
  | 'personas'
  | 'scenarios'
  | 'coachingHub'
  | 'myTeam'
  | 'analytics'
  | 'roleplayUsage'
  | 'organizations'
  | 'entitlements'
  | 'settings'
  | 'myOrganization'
  | 'bookings';

/** A top-level menu item, optionally shown only to roles holding a capability. */
export interface MenuEntry extends NavItem {
  i18nKey: MenuKey;
  requires?: AccessCapability;
}

export type VisibilityFilter = (typeof ADMIN_VISIBILITY_FILTERS)[number];

/** What a shared screen shows in a portal. Screens read these, never the base path. */
export interface PortalFeatures {
  /** Lists name each row's organization. */
  orgColumn: boolean;
  /** Lists offer the organization filter to a role holding `org.filter`. */
  orgFilter: boolean;
  /** Creating content asks for organization or personal visibility. */
  visibilityChoice: boolean;
  /** Options of the content visibility filter. */
  visibilityFilters: readonly VisibilityFilter[];
  /** Catalog the scenario list asks for; null leaves it to the server. */
  catalogScope: 'admin' | null;
}

/** Copy that differs per portal. */
export interface PortalLabels {
  averageScore: `dashboard:${string}`;
  skillMatrixTitle: `dashboard:${string}`;
  skillMatrixCaption: `dashboard:${string}`;
}

export interface PortalConfig<K extends string = PortalKey> {
  key: K;
  basePath: string;
  /** JWT role the portal's layout requires. */
  requiredRole: UserRole;
  /** Access capability needed to enter, on top of the JWT role. */
  capability?: AccessCapability;
  features: PortalFeatures;
  labels: PortalLabels;
  menu: readonly MenuEntry[];
}

/** Least privilege: a portal shows only what its config turns on. */
const NO_FEATURES: PortalFeatures = {
  orgColumn: false,
  orgFilter: false,
  visibilityChoice: false,
  visibilityFilters: ORG_VISIBILITY_FILTERS,
  catalogScope: null,
};

const TEAM_LABELS: PortalLabels = {
  averageScore: 'dashboard:stats.teamAverageScore',
  skillMatrixTitle: 'dashboard:cards.teamStageMatrix',
  skillMatrixCaption: 'dashboard:cards.teamStageMatrixDesc',
};

/** Builds a portal config; features left out stay off. */
export function definePortal<K extends string>(
  config: Omit<PortalConfig<K>, 'features' | 'labels'> & {
    features?: Partial<PortalFeatures>;
    labels?: PortalLabels;
  }
): PortalConfig<K> {
  return {
    ...config,
    features: { ...NO_FEATURES, ...config.features },
    labels: config.labels ?? TEAM_LABELS,
  };
}

/** Query param naming the page a session's back link returns to. */
export const SESSION_RETURN_PARAM = 'from';

/** Links inside a portal, built from its base path. */
export function portalPaths(basePath: string) {
  return {
    dashboard: `${basePath}/dashboard`,
    sessions: {
      detail: (id: string) => `${basePath}/sessions/${id}`,
      report: (id: string) => `${basePath}/sessions/${id}/report`,
    },
    users: {
      list: `${basePath}/users`,
      detail: (id: string) => `${basePath}/users/${id}`,
    },
    personas: {
      list: `${basePath}/personas`,
      detail: (id: string) => `${basePath}/personas/${id}`,
      create: `${basePath}/personas/new`,
      edit: (id: string) => `${basePath}/personas/${id}/edit`,
      editRoute: `${basePath}/personas/:uuid/edit`,
    },
    scenarios: {
      list: `${basePath}/scenarios`,
      detail: (id: string) => `${basePath}/scenarios/${id}`,
      create: `${basePath}/scenarios/new`,
      edit: (id: string) => `${basePath}/scenarios/${id}/edit`,
      editRoute: `${basePath}/scenarios/:id/edit`,
    },
    coachingHub: {
      list: `${basePath}/coaching-hub`,
      session: (id: string, from?: string) => {
        const path = `${basePath}/coaching-hub/${id}`;
        return from ? `${path}?${SESSION_RETURN_PARAM}=${encodeURIComponent(from)}` : path;
      },
    },
    bookings: `${basePath}/bookings`,
  };
}

const ADMIN = '/admin';
const ORG = '/org';
const USER = '/user';

const admin = portalPaths(ADMIN);
const org = portalPaths(ORG);
const user = portalPaths(USER);

const USER_MENU: readonly MenuEntry[] = [
  { label: 'Dashboard', path: user.dashboard, icon: LayoutDashboard, i18nKey: 'dashboard' },
  { label: 'Personas', path: user.personas.list, icon: Users, i18nKey: 'personas' },
  { label: 'Scenarios', path: user.scenarios.list, icon: FileText, i18nKey: 'scenarios' },
  {
    label: 'Coaching Hub',
    path: user.coachingHub.list,
    icon: Brain,
    badge: 'AI',
    i18nKey: 'coachingHub',
    requires: 'coaching.own',
  },
  {
    label: 'Bookings',
    path: user.bookings,
    icon: CalendarClock,
    i18nKey: 'bookings',
    requires: 'booking.own',
  },
  {
    label: 'My team',
    path: `${USER}/team`,
    icon: UserRound,
    i18nKey: 'myTeam',
    requires: 'team.view',
  },
  { label: 'Analytics', path: `${USER}/analytics`, icon: BarChart3, i18nKey: 'analytics' },
  {
    label: 'Usage',
    path: `${USER}/usage`,
    icon: Timer,
    i18nKey: 'roleplayUsage',
    requires: 'team.view',
  },
];

const ADMIN_MENU: readonly MenuEntry[] = [
  { label: 'Dashboard', path: admin.dashboard, icon: LayoutDashboard, i18nKey: 'dashboard' },
  {
    label: 'Organizations',
    path: `${ADMIN}/organizations`,
    icon: Building2,
    i18nKey: 'organizations',
  },
  { label: 'Users', path: admin.users.list, icon: GraduationCap, i18nKey: 'users' },
  { label: 'Personas', path: admin.personas.list, icon: Users, i18nKey: 'personas' },
  { label: 'Scenarios', path: admin.scenarios.list, icon: FileText, i18nKey: 'scenarios' },
  {
    label: 'Coaching Hub',
    path: admin.coachingHub.list,
    icon: Brain,
    badge: 'AI',
    i18nKey: 'coachingHub',
  },
  {
    label: 'Bookings',
    path: admin.bookings,
    icon: CalendarClock,
    i18nKey: 'bookings',
    requires: 'booking.manage',
  },
  { label: 'Analytics', path: `${ADMIN}/analytics`, icon: BarChart3, i18nKey: 'analytics' },
  {
    label: 'Usage',
    path: `${ADMIN}/usage`,
    icon: Timer,
    i18nKey: 'roleplayUsage',
  },
  {
    label: 'Permissions & quotas',
    path: `${ADMIN}/entitlements`,
    icon: ShieldCheck,
    i18nKey: 'entitlements',
  },
  {
    label: 'Settings',
    path: `${ADMIN}/settings`,
    icon: Settings,
    i18nKey: 'settings',
    children: [
      {
        label: 'Stage Collection',
        path: `${ADMIN}/settings/stage-collection`,
        icon: FolderTree,
        i18nKey: 'stageCollection',
      },
      { label: 'Stages', path: `${ADMIN}/settings/stages`, icon: Layers, i18nKey: 'stages' },
      { label: 'Skills', path: `${ADMIN}/settings/skills`, icon: Sparkles, i18nKey: 'skills' },
      {
        label: 'Observable Signals',
        path: `${ADMIN}/settings/observable-signals`,
        icon: ListChecks,
        i18nKey: 'observableSignals',
      },
      {
        label: 'System Configuration',
        path: `${ADMIN}/settings/system-configuration`,
        icon: SlidersHorizontal,
        i18nKey: 'systemConfiguration',
      },
      {
        label: 'ICE Server',
        path: `${ADMIN}/settings/ice-server`,
        icon: Server,
        i18nKey: 'iceServer',
      },
    ],
  },
];

const ORG_MENU: readonly MenuEntry[] = [
  { label: 'Dashboard', path: org.dashboard, icon: LayoutDashboard, i18nKey: 'dashboard' },
  {
    label: 'My organization',
    path: `${ORG}/organization`,
    icon: Building2,
    i18nKey: 'myOrganization',
  },
  { label: 'Personas', path: org.personas.list, icon: Users, i18nKey: 'personas' },
  { label: 'Scenarios', path: org.scenarios.list, icon: FileText, i18nKey: 'scenarios' },
  {
    label: 'Coaching Hub',
    path: org.coachingHub.list,
    icon: Brain,
    badge: 'AI',
    i18nKey: 'coachingHub',
  },
  {
    label: 'Bookings',
    path: org.bookings,
    icon: CalendarClock,
    i18nKey: 'bookings',
    requires: 'booking.manage',
  },
  { label: 'Analytics', path: `${ORG}/analytics`, icon: BarChart3, i18nKey: 'analytics' },
  {
    label: 'Usage',
    path: `${ORG}/usage`,
    icon: Timer,
    i18nKey: 'roleplayUsage',
  },
  {
    label: 'Permissions & quotas',
    path: `${ORG}/entitlements`,
    icon: ShieldCheck,
    i18nKey: 'entitlements',
  },
];

export const PORTALS = {
  admin: definePortal({
    key: 'admin',
    basePath: ADMIN,
    requiredRole: 'ADMIN',
    features: {
      orgColumn: true,
      orgFilter: true,
      visibilityFilters: ADMIN_VISIBILITY_FILTERS,
    },
    labels: {
      averageScore: 'dashboard:stats.systemAverageScore',
      skillMatrixTitle: 'dashboard:cards.systemSkillMatrix',
      skillMatrixCaption: 'dashboard:cards.systemSkillMatrixDesc',
    },
    menu: ADMIN_MENU,
  }),
  org: definePortal({
    key: 'org',
    basePath: ORG,
    requiredRole: 'USER',
    capability: 'org.portal',
    features: {
      visibilityChoice: true,
      catalogScope: 'admin',
    },
    labels: {
      averageScore: 'dashboard:stats.orgAverageScore',
      skillMatrixTitle: 'dashboard:cards.orgSkillMatrix',
      skillMatrixCaption: 'dashboard:cards.orgSkillMatrixDesc',
    },
    menu: ORG_MENU,
  }),
  user: definePortal({
    key: 'user',
    basePath: USER,
    requiredRole: 'USER',
    capability: 'practice',
    menu: USER_MENU,
  }),
} as const satisfies Record<PortalKey, PortalConfig>;

export function isUnderBasePath(pathname: string, basePath: string): boolean {
  return pathname === basePath || pathname.startsWith(`${basePath}/`);
}

/** The portal a path belongs to, if any. */
export function portalForPath<P extends PortalConfig<string>>(
  pathname: string,
  portals: readonly P[]
): P | undefined {
  return portals.find((portal) => isUnderBasePath(pathname, portal.basePath));
}
